import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { ArrowUpDown, Building2, ChevronRight, CircleCheck, CircleDotDashed, CirclePause, Layers, Search, SearchX, X } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { Skeleton } from '@/components/insights/Skeleton';
import { ListingSummary, ListingViewToggle, UnderlineTabs } from '@/components/Listing';
import { INDUSTRY_OPTIONS } from '@/config/industries';
import {
  industryConfigsService, IndustryStatus, IndustryTemplate, TemplateEntityType,
} from '@/services/industry-configs.service';
import { ENTITY_META, ENTITY_ORDER, GROUP_ICONS, STATUS_META, industryIcon, timeAgo } from './industry-meta';
import { StatusChip } from './StatusChip';

type View = 'grid' | 'list';
type Sort = 'group' | 'name' | 'status' | 'fields' | 'updated';
type StatusFilter = 'all' | IndustryStatus;

interface Row {
  key: string;
  label: string;
  group: string;
  status: IndustryStatus;
  counts: Record<TemplateEntityType, number>;
  total: number;
  companies: number;
  updatedAt?: string;
  copyPaused: boolean;
  haystack: string;
}

const GROUP_ORDER = Array.from(new Set(INDUSTRY_OPTIONS.map((o) => o.group)));
const SORTS: { value: Sort; label: string }[] = [
  { value: 'group', label: 'By group' },
  { value: 'name', label: 'Name A–Z' },
  { value: 'status', label: 'By status' },
  { value: 'fields', label: 'Most fields' },
  { value: 'updated', label: 'Recently updated' },
];
const STATUS_FILTERS: StatusFilter[] = ['all', 'in_progress', 'done', 'not_started'];
// Work in flight first, finished last.
const STATUS_RANK: Record<IndustryStatus, number> = { in_progress: 0, not_started: 1, done: 2 };
const VIEW_KEY = 'sa.industryConfigs.view';

function storedView(): View {
  try { return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid'; } catch { return 'grid'; }
}

function Highlight({ text, terms }: { text: string; terms: string[] }) {
  if (!terms.length) return <>{text}</>;
  const re = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'ig');
  return (
    <>
      {text.split(re).map((part, i) => (i % 2 === 1
        ? <mark key={i} className="rounded bg-primary-soft px-0.5 text-text-primary">{part}</mark>
        : <span key={i}>{part}</span>))}
    </>
  );
}

function IndustryIcon({ row, size = 'md' }: { row: Row; size?: 'md' | 'sm' }) {
  const Icon = industryIcon(row.key, row.group);
  return (
    <span className={clsx(
      'relative flex shrink-0 items-center justify-center bg-primary-soft text-primary',
      size === 'md' ? 'h-10 w-10 rounded-xl' : 'h-9 w-9 rounded-lg',
    )}>
      <Icon size={size === 'md' ? 19 : 17} strokeWidth={1.8} />
      {row.status === 'done' && (
        <span className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-surface">
          <CircleCheck size={14} className="text-success" strokeWidth={2.4} />
        </span>
      )}
    </span>
  );
}

function FieldCounts({ row, compact }: { row: Row; compact?: boolean }) {
  if (row.total === 0) return <span className="text-xs text-text-muted">No default fields yet</span>;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {ENTITY_ORDER.map((e) => {
        const n = row.counts[e];
        const Icon = ENTITY_META[e].icon;
        return (
          <span
            key={e}
            title={`${n} ${ENTITY_META[e].label.toLowerCase()} field${n === 1 ? '' : 's'}`}
            className={clsx(
              'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
              n > 0 ? 'bg-surface-alt text-text-secondary' : 'text-text-muted',
            )}
          >
            <Icon size={11} />
            {compact ? n : `${ENTITY_META[e].label} ${n}`}
          </span>
        );
      })}
    </div>
  );
}

export default function IndustryConfigs() {
  const nav = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const searchRef = useRef<HTMLInputElement>(null);
  const [templates, setTemplates] = useState<IndustryTemplate[] | null>(null);
  const [usage, setUsage] = useState<Record<string, number>>({});

  const q = params.get('q') ?? '';
  const statusParam = params.get('status');
  const statusF: StatusFilter = STATUS_FILTERS.includes(statusParam as StatusFilter) ? (statusParam as StatusFilter) : 'all';
  const groupParam = params.get('group');
  const group = groupParam && GROUP_ORDER.includes(groupParam) ? groupParam : 'all';
  const sortParam = params.get('sort');
  const sort: Sort = SORTS.some((s) => s.value === sortParam) ? (sortParam as Sort) : 'group';
  const viewParam = params.get('view');
  const view: View = viewParam === 'list' || viewParam === 'grid' ? viewParam : storedView();

  const setParam = (key: string, value: string, fallback: string) => {
    const next = new URLSearchParams(params);
    if (!value || value === fallback) next.delete(key); else next.set(key, value);
    setParams(next, { replace: true });
  };
  const setView = (v: View) => {
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage blocked — the URL still carries it */ }
    setParam('view', v, '');
  };

  useEffect(() => {
    industryConfigsService.list().then(setTemplates).catch(() => setTemplates([]));
    industryConfigsService.usage().then(setUsage).catch(() => setUsage({}));
  }, []);

  // "/" jumps to search.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement;
      const typing = el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
      if (e.key === '/' && !typing) { e.preventDefault(); searchRef.current?.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const rows = useMemo<Row[]>(() => {
    const byKey = new Map((templates ?? []).map((t) => [t.industryKey, t]));
    return INDUSTRY_OPTIONS.map((o) => {
      const t = byKey.get(o.value);
      const counts: Record<TemplateEntityType, number> = { lead: 0, contact: 0, deal: 0 };
      for (const f of t?.fields ?? []) counts[f.entityType] = (counts[f.entityType] ?? 0) + 1;
      return {
        key: o.value,
        label: o.label,
        group: o.group,
        status: t?.status ?? 'not_started',
        counts,
        total: t?.fields.length ?? 0,
        companies: usage[o.value] ?? 0,
        updatedAt: t?.updatedAt,
        copyPaused: !!t && t.isActive === false,
        haystack: [o.label, o.value, o.group, ...(t?.fields ?? []).map((f) => `${f.name} ${f.key}`)].join(' ').toLowerCase(),
      };
    });
  }, [templates, usage]);

  const terms = useMemo(() => q.toLowerCase().split(/\s+/).filter(Boolean), [q]);
  const searched = useMemo(
    () => rows.filter((r) => (group === 'all' || r.group === group) && terms.every((t) => r.haystack.includes(t))),
    [rows, group, terms],
  );
  const visible = useMemo(() => {
    const byName = (a: Row, b: Row) => a.label.localeCompare(b.label);
    return searched
      .filter((r) => statusF === 'all' || r.status === statusF)
      .sort((a, b) => {
        switch (sort) {
          case 'name': return byName(a, b);
          case 'status': return STATUS_RANK[a.status] - STATUS_RANK[b.status] || byName(a, b);
          case 'fields': return b.total - a.total || byName(a, b);
          case 'updated': return (b.updatedAt ?? '').localeCompare(a.updatedAt ?? '') || byName(a, b);
          default: return GROUP_ORDER.indexOf(a.group) - GROUP_ORDER.indexOf(b.group) || byName(a, b);
        }
      });
  }, [searched, statusF, sort]);
  const sections = useMemo(() => {
    if (sort !== 'group') return [{ group: '', rows: visible }];
    return GROUP_ORDER.map((g) => ({ group: g, rows: visible.filter((r) => r.group === g) })).filter((s) => s.rows.length > 0);
  }, [visible, sort]);

  const count = (s: IndustryStatus) => rows.filter((r) => r.status === s).length;
  const done = count('done');
  const donePct = Math.round((done / Math.max(rows.length, 1)) * 100);
  const withFields = rows.filter((r) => r.total > 0).length;
  const assigned = rows.reduce((n, r) => n + r.companies, 0);
  const filtersOn = !!q || group !== 'all' || statusF !== 'all';

  const changeStatus = async (row: Row, next: IndustryStatus) => {
    const before = templates;
    const stamp = new Date().toISOString();
    setTemplates((ts) => {
      const list = ts ?? [];
      return list.some((t) => t.industryKey === row.key)
        ? list.map((t) => (t.industryKey === row.key ? { ...t, status: next, updatedAt: stamp } : t))
        : [...list, { industryKey: row.key, label: row.label, fields: [], isActive: true, status: next, updatedAt: stamp }];
    });
    try {
      const saved = await industryConfigsService.setStatus(row.key, next, row.label);
      setTemplates((ts) => (ts ?? []).map((t) => (t.industryKey === row.key ? saved : t)));
      toast.success(`${row.label} marked ${STATUS_META[next].label.toLowerCase()}`);
    } catch {
      setTemplates(before);
    }
  };

  const openRow = (key: string) => nav(`/industry-configs/${key}`, { state: { from: location.search } });
  const clearFilters = () => {
    const next = new URLSearchParams();
    if (viewParam) next.set('view', viewParam);
    if (sort !== 'group') next.set('sort', sort);
    setParams(next, { replace: true });
  };

  return (
    <>
      <Topbar
        title="Industry Configs"
        breadcrumb="Catalog / Industry Configs"
        description="Default fields for every industry — copied into a company when you assign it that industry."
      />
      <div className="mx-auto w-full max-w-[1680px] space-y-6 px-4 py-6 sm:px-7 sm:py-8">
        <ListingSummary
          items={[
            { label: 'Industries', value: rows.length, detail: `Across ${GROUP_ORDER.length} groups`, icon: <Layers size={18} /> },
            {
              label: 'Done',
              value: templates ? done : '—',
              detail: (
                <span className="flex items-center gap-2">
                  <span className="h-1.5 w-16 overflow-hidden rounded-full bg-surface-alt">
                    <span className="block h-full rounded-full bg-success" style={{ width: `${donePct}%` }} />
                  </span>
                  {donePct}% of all industries
                </span>
              ),
              icon: <CircleCheck size={18} />,
            },
            { label: 'In progress', value: templates ? count('in_progress') : '—', detail: 'Being built right now', icon: <CircleDotDashed size={18} /> },
            { label: 'Companies assigned', value: templates ? assigned : '—', detail: `${withFields} industries have default fields`, icon: <Building2 size={18} /> },
          ]}
        />

        <div>
          <h2 className="text-lg font-semibold tracking-tight text-text-primary">Industry catalog</h2>
          <p className="mt-1 text-sm text-text-muted">Open an industry to edit its default fields. Use the status chip to track what's finished.</p>
        </div>

        <div className="rounded-2xl border border-border bg-surface px-4 pt-4 sm:px-5">
          <div className="flex flex-wrap items-center gap-3">
            <div className="min-w-[220px] flex-1">
              <Input
                ref={searchRef}
                aria-label="Search industries"
                iconLeft={<Search size={17} />}
                iconRight={q ? (
                  <button
                    type="button"
                    onClick={() => { setParam('q', '', ''); searchRef.current?.focus(); }}
                    className="rounded-md p-1.5 text-text-muted hover:bg-surface-alt hover:text-text-primary"
                    aria-label="Clear search"
                  >
                    <X size={14} />
                  </button>
                ) : (
                  <kbd className="mr-1.5 rounded border border-border bg-surface-alt px-1.5 font-mono text-[10px] text-text-muted">/</kbd>
                )}
                value={q}
                onChange={(e) => setParam('q', e.target.value, '')}
                onKeyDown={(e) => { if (e.key === 'Escape') { setParam('q', '', ''); e.currentTarget.blur(); } }}
                placeholder="Search industries, groups or field names…"
              />
            </div>
            <Select aria-label="Filter by group" value={group} onChange={(e) => setParam('group', e.target.value, 'all')} className="!w-auto">
              <option value="all">All groups</option>
              {GROUP_ORDER.map((g) => <option key={g} value={g}>{g}</option>)}
            </Select>
            <div className="flex items-center gap-1.5">
              <ArrowUpDown size={15} className="text-text-muted" aria-hidden />
              <Select aria-label="Sort industries" value={sort} onChange={(e) => setParam('sort', e.target.value, 'group')} className="!w-auto">
                {SORTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </Select>
            </div>
            <ListingViewToggle value={view} onChange={setView} />
          </div>
          <div className="mt-4">
            <UnderlineTabs
              label="Build status"
              value={statusF}
              onChange={(v) => setParam('status', v, 'all')}
              tabs={STATUS_FILTERS.map((s) => ({
                value: s,
                label: s === 'all' ? 'All industries' : STATUS_META[s].label,
                dot: s === 'all' ? undefined : STATUS_META[s].dot,
                count: templates ? (s === 'all' ? searched.length : searched.filter((r) => r.status === s).length) : undefined,
              }))}
            />
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 text-xs text-text-muted">
          <span>{templates ? `Showing ${visible.length} of ${rows.length} industries` : 'Loading industries…'}</span>
          {filtersOn && (
            <button type="button" onClick={clearFilters} className="font-medium text-primary hover:underline">Clear filters</button>
          )}
        </div>

        {!templates ? (
          view === 'grid' ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-surface p-5 shadow-soft">
                  <div className="flex justify-between"><Skeleton className="h-10 w-10 rounded-xl" /><Skeleton className="h-6 w-24 rounded-full" /></div>
                  <Skeleton className="mt-4 h-4 w-32" /><Skeleton className="mt-2 h-3 w-24" /><Skeleton className="mt-5 h-5 w-44" />
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2 rounded-2xl border border-border bg-surface p-4">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          )
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center rounded-2xl border border-dashed border-border bg-surface px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-alt text-text-muted"><SearchX size={22} /></span>
            <h3 className="mt-4 text-sm font-semibold text-text-primary">No industries match</h3>
            <p className="mt-1 max-w-sm text-sm text-text-muted">
              {q ? `Nothing matches “${q}”. Try a different word or clear the filters.` : 'No industry has this status yet.'}
            </p>
            <Button variant="secondary" size="sm" className="mt-4" onClick={clearFilters}>Clear filters</Button>
          </div>
        ) : view === 'grid' ? (
          <div className="space-y-8">
            {sections.map((s) => (
              <section key={s.group || 'all'} className="space-y-3">
                {s.group && <GroupHeader group={s.group} rows={rows.filter((r) => r.group === s.group)} />}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                  {s.rows.map((r, i) => (
                    <IndustryCard key={r.key} row={r} index={i} terms={terms} from={location.search}
                      onStatus={(next) => changeStatus(r, next)} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-border bg-surface shadow-soft">
            <table className="w-full min-w-[880px] text-sm">
              <thead>
                <tr className="border-b border-border bg-surface-alt/60 text-left text-2xs uppercase tracking-[0.14em] text-text-muted">
                  <th className="px-5 py-3 font-medium">Industry</th>
                  <th className="px-4 py-3 font-medium">Group</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Default fields</th>
                  <th className="px-4 py-3 text-right font-medium">Companies</th>
                  <th className="px-4 py-3 font-medium">Updated</th>
                  <th className="w-10 px-3 py-3" />
                </tr>
              </thead>
              <tbody>
                {sections.map((s) => (
                  <ListSection key={s.group || 'all'} group={s.group} allRows={rows} rows={s.rows} terms={terms}
                    onOpen={openRow} onStatus={changeStatus} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

function IndustryCard({ row: r, index, terms, from, onStatus }: {
  row: Row;
  index: number;
  terms: string[];
  from: string;
  onStatus: (next: IndustryStatus) => Promise<void>;
}) {
  return (
    // The entrance animation lives on a wrapper: its fill-mode transform would
    // otherwise cancel the card's hover lift.
    <div className="row-in" style={{ animationDelay: `${Math.min(index, 12) * 24}ms` }}>
      <div className="group relative flex h-full flex-col rounded-2xl border border-border bg-surface p-5 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-elevated">
        <div className="flex items-start justify-between gap-2">
          <IndustryIcon row={r} />
          <div className="relative z-10">
            <StatusChip status={r.status} onChange={onStatus} />
          </div>
        </div>
        <div className="mt-4 min-w-0">
          <h3 className="truncate text-[15px] font-semibold tracking-tight text-text-primary transition-colors group-hover:text-primary">
            <Highlight text={r.label} terms={terms} />
          </h3>
          <p className="mt-0.5 truncate font-mono text-[11px] text-text-muted">{r.key}</p>
        </div>
        <div className="mb-5 mt-3"><FieldCounts row={r} /></div>
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-border pt-3 text-xs text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Building2 size={13} />
            <span className={clsx('tabular-nums', r.companies > 0 && 'font-semibold text-text-primary')}>{r.companies}</span>
            compan{r.companies === 1 ? 'y' : 'ies'}
            {r.copyPaused && (
              <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-warning-soft px-1.5 py-0.5 text-2xs font-semibold text-warning" title="Fields are not copied to new companies">
                <CirclePause size={11} /> Paused
              </span>
            )}
          </span>
          <span>{r.updatedAt ? `Updated ${timeAgo(r.updatedAt)}` : 'Not configured'}</span>
        </div>
        {/* Stretched link: the whole card opens the industry; the status chip sits above it. */}
        <Link
          to={`/industry-configs/${r.key}`}
          state={{ from }}
          className="absolute inset-0 rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={`Open ${r.label}`}
        />
      </div>
    </div>
  );
}

function GroupHeader({ group, rows }: { group: string; rows: Row[] }) {
  const Icon = GROUP_ICONS[group] ?? Layers;
  const done = rows.filter((r) => r.status === 'done').length;
  return (
    <div className="flex items-center gap-3">
      <Icon size={16} className="text-text-muted" />
      <h3 className="text-sm font-semibold text-text-primary">{group}</h3>
      <span className="text-xs tabular-nums text-text-muted">{done} of {rows.length} done</span>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function ListSection({ group, allRows, rows, terms, onOpen, onStatus }: {
  group: string;
  allRows: Row[];
  rows: Row[];
  terms: string[];
  onOpen: (key: string) => void;
  onStatus: (row: Row, next: IndustryStatus) => Promise<void>;
}) {
  const inGroup = allRows.filter((r) => r.group === group);
  return (
    <>
      {group && (
        <tr className="border-b border-border bg-surface-alt/40">
          <td colSpan={7} className="px-5 py-2 text-xs font-semibold text-text-secondary">
            {group}
            <span className="ml-2 font-normal tabular-nums text-text-muted">
              {inGroup.filter((r) => r.status === 'done').length} of {inGroup.length} done
            </span>
          </td>
        </tr>
      )}
      {rows.map((r) => (
        <tr
          key={r.key}
          onClick={() => onOpen(r.key)}
          className="cursor-pointer border-b border-border transition-colors last:border-b-0 hover:bg-surface-alt/50"
        >
          <td className="px-5 py-3">
            <div className="flex items-center gap-3">
              <IndustryIcon row={r} size="sm" />
              <div className="min-w-0">
                <Link
                  to={`/industry-configs/${r.key}`}
                  state={{ from: window.location.search }}
                  onClick={(e) => e.stopPropagation()}
                  className="block truncate font-medium text-text-primary hover:text-primary"
                >
                  <Highlight text={r.label} terms={terms} />
                </Link>
                <span className="block truncate font-mono text-[11px] text-text-muted">{r.key}</span>
              </div>
            </div>
          </td>
          <td className="px-4 py-3 text-text-secondary">{r.group}</td>
          <td className="px-4 py-3"><StatusChip size="sm" status={r.status} onChange={(next) => onStatus(r, next)} /></td>
          <td className="px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="w-5 text-right font-semibold tabular-nums text-text-primary">{r.total}</span>
              {r.total > 0 && <FieldCounts row={r} compact />}
              {r.copyPaused && <CirclePause size={13} className="text-warning" aria-label="Copy paused" />}
            </div>
          </td>
          <td className={clsx('px-4 py-3 text-right tabular-nums', r.companies > 0 ? 'font-semibold text-text-primary' : 'text-text-muted')}>{r.companies}</td>
          <td className="px-4 py-3 text-xs text-text-muted">{timeAgo(r.updatedAt)}</td>
          <td className="px-3 py-3 text-text-muted"><ChevronRight size={16} /></td>
        </tr>
      ))}
    </>
  );
}
